import React from "react";
import { ReactSVG } from "react-svg";

// region Libraries
import { Divider } from "@material-ui/core";
import _ from "lodash";
// endregion Libraries
// region Languages
import useTranslation from "src/translations/useTranslation";
import { TLanguages } from "@shared/languages/languageData";
import { GlobalMessages, VehicleAndTravelDetailsMessages } from "@shared/languages/interfaces";
// endregion Languages
// region Interfaces
import { WaterAddition as WaterAdditionInterface } from "@shared/interfaces/water-addition.interface";
// endregion Interfaces
// region Assets
import IconWater from "@assets/icons/water.svg";
// endregion Assets
// region Utils
import utils from "../../../utils/useful-functions";
// endregion Utils
// region Styles
import { Container } from "./styles";
// endregion Styles

type WaterAdditionItemProps = { waterAdditions: WaterAdditionInterface[]; }
const WaterAdditionModalList: React.FC<WaterAdditionItemProps> = ({ waterAdditions }) => {

  const { t, i18n } = useTranslation();

  // Three buckets: normal additions of this travel, post-discharge reassigned IN from the previous
  // travel, and post-discharge that originated here but was reused in the next travel (reassigned AWAY).
  const normalAdditions = waterAdditions.filter((wa) => !wa.is_reassigned_post_discharge && !wa.is_reassigned_away);
  const reassignedInAdditions = waterAdditions.filter((wa) => wa.is_reassigned_post_discharge && !wa.is_reassigned_away);
  const reassignedAwayAdditions = waterAdditions.filter((wa) => wa.is_reassigned_away);

  const renderItem = (waterAddition: WaterAdditionInterface, kind: "normal" | "in" | "away" = "normal") => (
    <div key={waterAddition.id_water_addition} id={waterAddition.id_water_addition}>
      <div className="waterAddition">
        <div className="boxHeaderItemsEvent">
          <div className="content">
            <div className="boxHeaderItemsIcon">
              <ReactSVG src={IconWater} />
            </div>
            <div className="boxHeaderItemsDescription">
              <div className="heading typeAndDuration">
                <div
                  className="type"
                  style={{ color: "#000" }}
                >
                  {t(GlobalMessages.quantity)}
                  {kind === "in" && (
                    <span className="badgePost">{t(VehicleAndTravelDetailsMessages.waterAdditionReassignedBadge)}</span>
                  )}
                </div>
                <div className="duration">
                  {utils.formatNumberToLocaleString(waterAddition.water_added, i18n.language as TLanguages, 2)}L
                </div>
              </div>
              {waterAddition?.start_date && (
                <div className="heading startDate">
                  <div>{t(GlobalMessages.time)}:</div>
                  <div>{utils.formatDateIfHave(waterAddition.start_date, "time")}</div>
                </div>
              )}
              {kind === "in" && (
                <div className="heading moment">
                  <div>{t(VehicleAndTravelDetailsMessages.waterAdditionOriginTravel)}:</div>
                  <div>{waterAddition.origin_travel_document || t(VehicleAndTravelDetailsMessages.waterAdditionAutomaticTravel)}</div>
                </div>
              )}
              {kind === "away" && (
                <div className="heading moment">
                  <div>{t(VehicleAndTravelDetailsMessages.waterAdditionDestinationTravel)}:</div>
                  <div>{waterAddition.destination_travel_document || t(VehicleAndTravelDetailsMessages.waterAdditionAutomaticTravel)}</div>
                </div>
              )}
              {kind === "normal" && waterAddition?.is_post_discharge !== undefined && (
                <div className="heading moment">
                  <div>{t(GlobalMessages.moment)}:</div>
                  <div>
                    {
                      waterAddition?.is_post_discharge
                        ? t(VehicleAndTravelDetailsMessages.waterAdditionPostDischarge)
                        : t(VehicleAndTravelDetailsMessages.waterAdditionPreDischarge)
                    }
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <br />
      <Divider />
    </div>
  );

  return (
    <Container>
      {!_.isEmpty(normalAdditions) && normalAdditions.map((waterAddition) => renderItem(waterAddition))}
      {!_.isEmpty(reassignedInAdditions) && (
        <div className="reassignedBlock">
          <div className="reassignedTitle">{t(VehicleAndTravelDetailsMessages.waterAdditionReassignedTitle)}</div>
          {reassignedInAdditions.map((waterAddition) => renderItem(waterAddition, "in"))}
        </div>
      )}
      {!_.isEmpty(reassignedAwayAdditions) && (
        <div className="reassignedBlock awayBlock">
          <div className="reassignedTitle">{t(VehicleAndTravelDetailsMessages.waterAdditionReassignedAwayTitle)}</div>
          {reassignedAwayAdditions.map((waterAddition) => renderItem(waterAddition, "away"))}
        </div>
      )}
    </Container>
  );
};

export default WaterAdditionModalList;
